import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Card } from 'react-bootstrap';
import { InAppTemplate } from '../../../../shared/templates/inapp';
import { LightspeedConnectButton, StoreConnectionStatus } from '../../../../shared/components/Lightspeed';
import { LightspeedErrorNotification } from '../../../../shared/components/Lightspeed/LightspeedErrorNotification';
import { LightspeedError, handleLightspeedError, LightspeedErrorHandler } from '../../../../shared/utils/lightspeedErrorHandler';
import '../integrations.scss';

export const LightspeedIntegrationPage = () => {
  return (
    <InAppTemplate 
      pageHeadingTitle="Lightspeed Integration" 
      childComponent={<LightspeedIntegrationContent />} 
    />
  );
};

const LightspeedIntegrationContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [lightspeedError, setLightspeedError] = useState<LightspeedError | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  
  const getStoreId = () => {
    try {
      const loggedAccountInfo = JSON.parse(localStorage.getItem("mamiVendorXAuth") || "{}");
      return loggedAccountInfo?.selectedStore?.id || null;
    } catch (error) {
      return null;
    }
  };
  
  const storeId = getStoreId();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');
    const state = location.state as any;

    if (success === 'connected' || state?.success) {
      // We came back from OAuth with success; now verify via connection status
      setVerificationPending(true);
      setNotification({
        type: 'success',
        message: 'Verifying Lightspeed connection…'
      });
      setLightspeedError(null);
      navigate('/app/integrations/lightspeed', { replace: true });
    } else if (error) {
      const lightspeedErr = LightspeedErrorHandler.mapOAuthErrorToLightspeedError(
        error, 
        errorDescription || undefined
      );
      setLightspeedError(lightspeedErr);
      setVerificationPending(false);
      setNotification(null);
      navigate('/app/integrations/lightspeed', { replace: true });
    } else if (state?.error) {
      try {
        const lightspeedErr = handleLightspeedError(new Error(state.message || 'Connection failed'));
        setLightspeedError(lightspeedErr);
        setVerificationPending(false);
      } catch (err) {
        setNotification({
          type: 'error',
          message: state.message || 'An error occurred'
        });
      }
    }
  }, [location, navigate]);

  // Once status check confirms connection, show final success message
  useEffect(() => {
    if (verificationPending && isConnected) {
      setNotification({
        type: 'success',
        message: 'Lightspeed integration completed'
      });
      setLightspeedError(null);
      setVerificationPending(false);
    }
  }, [verificationPending, isConnected]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleRetryConnection = () => {
    setLightspeedError(null);
    window.location.reload();
  };

  const handleDismissError = () => {
    setLightspeedError(null);
  };

  const handleConnectError = (error: Error) => {
    const lightspeedErr = handleLightspeedError(error);
    setLightspeedError(lightspeedErr);
    setNotification(null);
  };

  return (
    <div className="lightspeed-integration-page">
      <div className="page-content">
        {lightspeedError && (
          <LightspeedErrorNotification
            error={lightspeedError}
            onRetry={lightspeedError.retryable ? handleRetryConnection : undefined}
            onDismiss={handleDismissError}
            className="mb-4"
          />
        )}

        {notification && !lightspeedError && (
          <Alert 
            variant={notification.type === 'success' ? 'success' : 'danger'}
            dismissible
            onClose={() => setNotification(null)}
            className="mb-4"
          >
            {notification.message}
          </Alert>
        )}

        <div className="integration-overview mb-4">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <div className="integration-logo me-3">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <rect width="48" height="48" rx="8" fill="#0066CC"/>
                    <path d="M12 16h24v16H12V16z" fill="white"/>
                    <path d="M16 20h16v2H16v-2zm0 4h12v2H16v-2zm0 4h8v2H16v-2z" fill="#0066CC"/>
                  </svg>
                </div>
                <div>
                  <h4 className="mb-1">Lightspeed Retail Integration</h4>
                  <p className="text-muted mb-0">
                    Connect your MamiMarkets store with Lightspeed POS to sync inventory, orders, and customer data.
                  </p>
                </div>
              </div>
              
              <div className="integration-features">
                <h6>Features:</h6>
                <ul className="list-unstyled">
                  <li>✅ Real-time inventory synchronization</li>
                  <li>✅ Automatic order management</li>
                  <li>✅ Customer data sync</li>
                  <li>✅ Product catalog integration</li>
                </ul>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="connection-management">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Connection Status</h5>
            </Card.Header>
            <Card.Body>
              <StoreConnectionStatus storeId={storeId} onStatusChange={(connected) => setIsConnected(connected)} />

              {!isConnected && (
                <div className="mt-4">
                  <h6>Connect Your Store</h6>
                  <p className="text-muted mb-3">
                    Click the button below to start the connection process with Lightspeed.
                    You'll be redirected to Lightspeed to authorize the connection.
                  </p>

                  <LightspeedConnectButton
                    storeId={storeId}
                    onSuccess={() => {
                      setNotification({
                        type: 'success',
                        message: 'Redirecting to Lightspeed for authorization...'
                      });
                      setLightspeedError(null);
                    }}
                    onError={handleConnectError}
                    className="me-2"
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="integration-help mt-4">
          <Card>
            <Card.Header>
              <h5 className="mb-0">Need Help?</h5>
            </Card.Header>
            <Card.Body>
              <div className="row">
                <div className="col-md-6">
                  <h6>Setup Instructions</h6>
                  <ol>
                    <li>Click "Connect to Lightspeed" above</li>
                    <li>Log in to your Lightspeed account</li>
                    <li>Authorize MamiMarkets to access your data</li>
                    <li>Return here to verify the connection</li>
                  </ol>
                </div>
                <div className="col-md-6">
                  <h6>Troubleshooting</h6>
                  <ul>
                    <li>Ensure you have admin access to your Lightspeed account</li>
                    <li>Check that your Lightspeed subscription is active</li>
                    <li>Contact support if connection issues persist</li>
                  </ul>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LightspeedIntegrationPage;